import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { useSEO } from '../hooks/useSEO';
import { getEvidenceById } from '../lib/evidence';
import { SCORE_PAGES } from '../lib/site';
import { scoreContentFor } from '../lib/scoreContent';
import { MEEM_TOTAL_MAX } from '../lib/meemItems';

const brucki = getEvidenceById('brucki-2003');
const lourenco = getEvidenceById('lourenco-veras-2006');
const REFERENCES = [brucki, lourenco].map((study) => `${study.citation} DOI: ${study.primarySource.value}.`);

const RELATED = [
  { to: '/escolaridade', label: 'Mini Mental por escolaridade', note: 'veja a tabela completa de medianas por faixa de escolaridade.' },
  { to: '/pontos-de-corte', label: 'Ponto de corte do Mini Mental', note: 'entenda por que mediana e ponto de corte respondem perguntas diferentes.' },
  { to: '/interpretacao-dos-resultados', label: 'Como interpretar o resultado', note: 'o que o escore responde e o que ele não responde sozinho.' }
];

/** Páginas não publicadas individualmente: conteúdo genérico, deliberadamente noindex — nunca uma página fina indexável. */
function GenericScoreNotice({ score }) {
  useSEO({
    title: Number.isFinite(score) ? `MEEM ${score}/${MEEM_TOTAL_MAX} — MiniMental.com.br` : 'Pontuação não encontrada — MiniMental.com.br',
    description: 'Interpretação geral de uma pontuação do Mini Mental ainda sem página individualizada publicada.',
    noindex: true
  });
  const published = SCORE_PAGES.filter((p) => Number.isFinite(p.score)).sort((a, b) => Math.abs(a.score - score) - Math.abs(b.score - score));
  return (
    <main className="mx-auto max-w-[680px] px-5 py-14">
      <h1 className="mb-4 text-2xl font-semibold text-[var(--primary)]">
        {Number.isFinite(score) ? `MEEM ${score}/${MEEM_TOTAL_MAX}` : 'Pontuação do Mini Mental'}
      </h1>
      <p className="text-[var(--text-muted)]">
        Ainda não publicamos uma interpretação individualizada para esta pontuação. A leitura de qualquer total do MEEM segue o mesmo princípio: compare com a mediana do grupo de escolaridade correspondente, nunca com um ponto de corte fixo, e nunca isoladamente.
      </p>
      <p className="mt-3 text-[var(--text-muted)]">
        Veja <Link to="/escolaridade" className="text-[var(--primary)] underline">Mini Mental por escolaridade</Link> para a tabela completa de medianas, ou{' '}
        <Link to="/pontos-de-corte" className="text-[var(--primary)] underline">Ponto de corte do Mini Mental</Link> para entender as referências disponíveis.
      </p>
      {published.length > 0 && (
        <p className="mt-6 text-[var(--text-muted)]">
          Pontuações com interpretação detalhada já publicada:{' '}
          {published.map((p, i) => (
            <React.Fragment key={p.path}>
              {i > 0 && ', '}
              <Link to={p.path} className="text-[var(--primary)] underline">{p.score} pontos</Link>
            </React.Fragment>
          ))}.
        </p>
      )}
      <p className="mt-6"><Link to="/" className="text-[var(--primary)] underline">Calcular o Mini Mental →</Link></p>
    </main>
  );
}

export default function ResultadoPontuacao() {
  const { slug } = useParams();
  const match = /^(\d+)-pontos$/.exec(String(slug || ''));
  const score = match ? Number(match[1]) : null;
  const page = SCORE_PAGES.find((p) => p.path === `/resultado/${slug}`);
  const content = page ? scoreContentFor(page.score) : null;

  useEffect(() => {
    if (page && content) window.eidocAnalytics?.capture('assessment_educational_page_viewed', {});
  }, [page, content]);

  if (!page || !content) return <GenericScoreNotice score={score} />;

  const answer = `Uma pontuação de ${page.score}/${MEEM_TOTAL_MAX} no Mini Mental precisa ser lida junto com a escolaridade: ${content.resumo}`;

  return (
    <ArticleLayout page={page} answer={answer} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="comparacao-por-escolaridade">
        <h2 id="comparacao-por-escolaridade" className="text-2xl mb-5">Como {page.score} se compara à mediana de cada escolaridade?</h2>
        <p className="text-[var(--text-muted)] mb-5">
          No estudo brasileiro de Brucki et al. (2003), com {brucki.sampleSize} indivíduos saudáveis, as medianas por escolaridade foram:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse border border-[var(--border)] rounded-xl overflow-hidden">
            <thead className="bg-[var(--surface-soft)]">
              <tr>
                <th className="p-4 border border-[var(--border)] font-bold">Escolaridade</th>
                <th className="p-4 border border-[var(--border)] font-bold text-right">Mediana observada</th>
                <th className="p-4 border border-[var(--border)] font-bold">{page.score} ficou</th>
              </tr>
            </thead>
            <tbody>
              {content.comparacaoEscolaridade.map((row) => (
                <tr key={row.grupo}>
                  <td className="p-4 border border-[var(--border)]">{row.grupo}</td>
                  <td className="p-4 border border-[var(--border)] text-right font-bold">{String(row.mediana).replace('.', ',')}</td>
                  <td className="p-4 border border-[var(--border)]">{row.posicao} da mediana</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[var(--text-muted)] mt-5">A mediana divide o grupo ao meio e não é um ponto de corte diagnóstico.</p>
      </section>

      <section aria-labelledby="divisao-de-rastreio">
        <h2 id="divisao-de-rastreio" className="text-2xl mb-5">E na divisão de rastreio de Lourenço e Veras (2006)?</h2>
        <p className="text-[var(--text-muted)] mb-5">
          Este estudo avaliou {lourenco.sampleSize} pessoas com mais de 65 anos em contexto ambulatorial e mediu sensibilidade e especificidade de divisões por escolaridade.
        </p>
        <p className="text-[var(--text-muted)]">{content.lourenco.texto}</p>
      </section>

      <section aria-labelledby="como-ler">
        <h2 id="como-ler" className="text-2xl mb-5">Como ler esse resultado</h2>
        <p className="text-[var(--text-muted)]">{content.leitura}</p>
        <ul className="mt-5 space-y-3 text-[var(--text-muted)] list-disc pl-5">
          {content.orientacoesGerais.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>
    </ArticleLayout>
  );
}
